import React, { useMemo } from 'react';
import useWebsiteAnalytics from '@/hooks/useWebsiteAnalytics';
import ReactApexChart from 'react-apexcharts';
import Layout from '@components/Layout';

/** Tiny tooltip helper (Bootstrap 5 or native title attr) */
const Info = ({ title }) => (
  <span
    className='ms-1 text-muted'
    data-bs-toggle='tooltip'
    title={title}
    aria-label={title}
    style={{ cursor: 'help' }}
  >
    ⓘ
  </span>
);

export default function WebsiteAnalytics() {
  const {
    from,
    to,
    setFrom,
    setTo,
    loading,
    err,
    summary, // { events, visitors, sessions, pageViews, avgPageTimeMs?, bounceRate?, errorRate? }
    topPages, // [{ path, count }]
    timeseries, // [{ date, pageViews, avgPageTimeMs?, errors? }]
    recent, // events array
    // NEW (add these in your hook once backend is ready)
    scrollDist = {}, // {25:n, 50:n, 75:n, 100:n}
    outboundTopHosts = [], // [{ host, count }]
    downloadsTop = [], // [{ path, count }]
    searchTerms = [], // [{ q, count }]
    featureSeen = [], // [{ name, count }]
    webVitals = {
      // { LCP:{avg,count,good,needs,poor}, CLS:{...}, ...}
      LCP: null,
      CLS: null,
      INP: null,
      FCP: null,
      TTFB: null,
    },
    navTimingAvg = null, // { dcl, load, ttfb, dns, tcp, tls, transfer }
    refresh,
  } = useWebsiteAnalytics(7);

  // Timeseries: Page Views (existing)
  const pvSeries = [
    { name: 'Page Views', data: timeseries.map((p) => p.pageViews || 0) },
  ];
  const pvOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    xaxis: { categories: timeseries.map((p) => p.date) },
    stroke: { curve: 'smooth', width: 3 },
  };

  // Timeseries: Avg Page Time (ms → s)
  const aptSeries = [
    {
      name: 'Avg Page Time (s)',
      data: timeseries.map(
        (p) => Math.round((p.avgPageTimeMs || 0) / 100) / 10
      ),
    },
  ];
  const aptOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    xaxis: { categories: timeseries.map((p) => p.date) },
    stroke: { curve: 'smooth', width: 3 },
  };

  // Timeseries: Errors per day
  const errSeries = [
    { name: 'Errors', data: timeseries.map((p) => p.errors || 0) },
  ];
  const errOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    xaxis: { categories: timeseries.map((p) => p.date) },
    stroke: { curve: 'smooth', width: 3 },
  };

  // Web vitals stacked bars (good/needs/poor per metric)
  const vitalNames = ['LCP', 'CLS', 'INP', 'FCP', 'TTFB'];
  const vitalGood = vitalNames.map((n) => webVitals?.[n]?.good || 0);
  const vitalNeeds = vitalNames.map(
    (n) => webVitals?.[n]?.['needs-improvement'] || 0
  );
  const vitalPoor = vitalNames.map((n) => webVitals?.[n]?.poor || 0);
  const vitalsSeries = [
    { name: 'Good', data: vitalGood },
    { name: 'Needs-Improvement', data: vitalNeeds },
    { name: 'Poor', data: vitalPoor },
  ];
  const vitalsOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    xaxis: { categories: vitalNames },
    plotOptions: { bar: { columnWidth: '40%' } },
  };

  const formatMs = (ms) => {
    if (!ms && ms !== 0) return '—';
    if (ms < 1000) return `${ms} ms`;
    const s = ms / 1000;
    return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  };

  const bounce =
    summary?.bounceRate != null
      ? `${Math.round(summary.bounceRate * 100)}%`
      : '—';
  const errRate =
    summary?.errorRate != null
      ? `${Math.round(summary.errorRate * 100)}%`
      : '—';
  const avgPageTime =
    summary?.avgPageTimeMs != null ? formatMs(summary.avgPageTimeMs) : '—';

  // Prepare scroll distribution
  const scrollRows = [25, 50, 75, 100].map((p) => ({
    p,
    n: scrollDist?.[p] || 0,
  }));

  return (
    <Layout isAdminPage={true}>
      {/* breadcrumbs */}
      <div className='row page-titles'>
        <div className='col-lg-8'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Administration</li>
            <li className='breadcrumb-item'>Analytics</li>
            <li className='breadcrumb-item'>Website</li>
          </ol>
        </div>
        <div className='col-lg-4' />
      </div>

      <div className='col-xl-12 col-lg-4'>
        {/* Date controls */}
        <div className='d-flex gap-2 align-items-center mb-3'>
          <label className='form-label mb-0'>From</label>
          <input
            type='date'
            className='form-control'
            value={from.toISOString().slice(0, 10)}
            onChange={(e) => setFrom(new Date(`${e.target.value}T00:00:00`))}
            style={{ maxWidth: 180 }}
          />
          <label className='form-label mb-0'>To</label>
          <input
            type='date'
            className='form-control'
            value={to.toISOString().slice(0, 10)}
            onChange={(e) => setTo(new Date(`${e.target.value}T23:59:59`))}
            style={{ maxWidth: 180 }}
          />
          <button
            className='btn btn-primary'
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {err && (
            <span className='text-danger ms-2'>
              {String(err.message || err)}
            </span>
          )}
        </div>

        {/* KPI cards (with tooltips) */}
        <div className='row g-3 mb-4'>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Events
                  <Info title='Total events captured (page views, clicks, timings, etc.)' />
                </div>
                <div className='fs-4 fw-bold'>{summary?.events ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Visitors
                  <Info title='Unique visitor IDs in the period (client-side UUID, not PII).' />
                </div>
                <div className='fs-4 fw-bold'>{summary?.visitors ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Sessions
                  <Info title='Distinct sessions (sessionStorage-based) across the period.' />
                </div>
                <div className='fs-4 fw-bold'>{summary?.sessions ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Page Views
                  <Info title='Count of page_view events.' />
                </div>
                <div className='fs-4 fw-bold'>{summary?.pageViews ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Avg Page Time
                  <Info title='Average engaged time on page (based on visibility + route timing).' />
                </div>
                <div className='fs-4 fw-bold'>{avgPageTime}</div>
              </div>
            </div>
          </div>
          <div className='col-sm-2'>
            <div className='card'>
              <div className='card-body'>
                <div className='text-muted'>
                  Error Rate
                  <Info title='error events divided by total events for the period.' />
                </div>
                <div className='fs-4 fw-bold'>{errRate}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeseries: Page Views */}
        <div className='card mb-4'>
          <div className='card-body'>
            <h5 className='card-title'>Daily Page Views</h5>
            <ReactApexChart
              options={pvOptions}
              series={pvSeries}
              type='line'
              height={280}
            />
          </div>
        </div>

        {/* Timeseries: Avg Page Time & Errors */}
        <div className='row g-4 mb-4'>
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Daily Avg Page Time (s)</h5>
                <ReactApexChart
                  options={aptOptions}
                  series={aptSeries}
                  type='line'
                  height={260}
                />
              </div>
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Daily Errors</h5>
                <ReactApexChart
                  options={errOptions}
                  series={errSeries}
                  type='line'
                  height={260}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='row g-4'>
          {/* Top Pages */}
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Top Pages</h5>
                <div className='table-responsive'>
                  <table className='table table-sm align-middle'>
                    <thead>
                      <tr>
                        <th>Path</th>
                        <th className='text-end'>Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPages.map((r) => (
                        <tr key={r.path}>
                          <td>{r.path}</td>
                          <td className='text-end'>{r.count}</td>
                        </tr>
                      ))}
                      {!topPages.length && (
                        <tr>
                          <td colSpan={2} className='text-muted'>
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Events */}
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Recent Events</h5>
                <div
                  className='table-responsive'
                  style={{ maxHeight: 360, overflowY: 'auto' }}
                >
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Type</th>
                        <th>Path</th>
                        <th>Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((ev) => (
                        <tr key={ev.id}>
                          <td>{ev.ts?.toDate?.().toLocaleString?.() || '—'}</td>
                          <td>{ev.type}</td>
                          <td
                            className='text-truncate'
                            style={{ maxWidth: 220 }}
                          >
                            {ev.path}
                          </td>
                          <td
                            className='text-truncate'
                            style={{ maxWidth: 140 }}
                          >
                            {ev.sessionId}
                          </td>
                        </tr>
                      ))}
                      {!recent.length && (
                        <tr>
                          <td colSpan={4} className='text-muted'>
                            No events
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll depth & Outbound/Downloads */}
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>
                  Scroll Depth
                  <Info title='How far visitors scrolled on pages during the period.' />
                </h5>
                <div className='table-responsive'>
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Milestone</th>
                        <th className='text-end'>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scrollRows.map((r) => (
                        <tr key={r.p}>
                          <td>{r.p}%</td>
                          <td className='text-end'>{r.n}</td>
                        </tr>
                      ))}
                      {!scrollRows.some((x) => x.n > 0) && (
                        <tr>
                          <td colSpan={2} className='text-muted'>
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className='row mt-3'>
                  <div className='col-sm-6'>
                    <h6>Top Outbound Hosts</h6>
                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>Host</th>
                            <th className='text-end'>Clicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outboundTopHosts.map((h) => (
                            <tr key={h.host}>
                              <td>{h.host}</td>
                              <td className='text-end'>{h.count}</td>
                            </tr>
                          ))}
                          {!outboundTopHosts.length && (
                            <tr>
                              <td colSpan={2} className='text-muted'>
                                No data
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <h6>Top Downloads</h6>
                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>Path</th>
                            <th className='text-end'>Downloads</th>
                          </tr>
                        </thead>
                        <tbody>
                          {downloadsTop.map((d) => (
                            <tr key={d.path}>
                              <td
                                className='text-truncate'
                                style={{ maxWidth: 220 }}
                              >
                                {d.path}
                              </td>
                              <td className='text-end'>{d.count}</td>
                            </tr>
                          ))}
                          {!downloadsTop.length && (
                            <tr>
                              <td colSpan={2} className='text-muted'>
                                No data
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Feature impressions */}
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>Site Search</h5>
                <div className='table-responsive'>
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Query</th>
                        <th className='text-end'>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchTerms.map((s) => (
                        <tr key={s.q}>
                          <td
                            className='text-truncate'
                            style={{ maxWidth: 240 }}
                          >
                            {s.q}
                          </td>
                          <td className='text-end'>{s.count}</td>
                        </tr>
                      ))}
                      {!searchTerms.length && (
                        <tr>
                          <td colSpan={2} className='text-muted'>
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <h5 className='card-title mt-4'>Feature Impressions</h5>
                <div className='table-responsive'>
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th className='text-end'>Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {featureSeen.map((f) => (
                        <tr key={f.name}>
                          <td>{f.name}</td>
                          <td className='text-end'>{f.count}</td>
                        </tr>
                      ))}
                      {!featureSeen.length && (
                        <tr>
                          <td colSpan={2} className='text-muted'>
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Performance: Web Vitals + Nav timing */}
          <div className='col-lg-6'>
            <div className='card h-100'>
              <div className='card-body'>
                <h5 className='card-title'>
                  Web Vitals
                  <Info title="Distribution of 'good / needs-improvement / poor' per metric." />
                </h5>
                <ReactApexChart
                  options={vitalsOptions}
                  series={vitalsSeries}
                  type='bar'
                  height={280}
                />

                <div className='row mt-3'>
                  {['LCP', 'CLS', 'INP', 'FCP', 'TTFB'].map((n) => {
                    const v = webVitals?.[n];
                    const avg =
                      v?.avg != null
                        ? n === 'CLS'
                          ? (v.avg / 1000).toFixed(3)
                          : Math.round(v.avg)
                        : '—';
                    const label =
                      n === 'CLS' ? 'avg (unitless x1000)' : 'avg (ms)';
                    return (
                      <div className='col-6 col-md-4 mb-2' key={n}>
                        <div className='small text-muted'>
                          {n} {label}
                        </div>
                        <div className='fw-semibold'>{avg}</div>
                      </div>
                    );
                  })}
                </div>

                <h6 className='mt-3'>Navigation Timing (avg)</h6>
                <div className='row'>
                  {[
                    ['TTFB', navTimingAvg?.ttfb],
                    ['DNS', navTimingAvg?.dns],
                    ['TCP', navTimingAvg?.tcp],
                    ['TLS', navTimingAvg?.tls],
                    ['Transfer', navTimingAvg?.transfer],
                    ['DCL', navTimingAvg?.dcl],
                    ['Load', navTimingAvg?.load],
                  ].map(([k, v]) => (
                    <div className='col-6 col-md-4 mb-2' key={k}>
                      <div className='small text-muted'>{k}</div>
                      <div className='fw-semibold'>{formatMs(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* (Optional) Recent Errors panel could go here when you add it to the hook */}
        </div>
      </div>
    </Layout>
  );
}
